/**
 * Sticky glass top bar: the page's title, subtitle and back link on the left
 * (each page's PageHead renders into the slot), the signed-in user and their
 * menu (theme, password for Local accounts, sign out) on the right.
 */

import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { ChevronDown, KeyRound, LogOut, Moon, Sun, UserRound } from 'lucide-react';
import { Link } from 'react-router-dom';

import { useSignOut } from '@/api/queries';
import type { Role } from '@/api/types';
import { ChangePasswordDialog } from '@/routes/account/ChangePasswordDialog';
import { useSession } from '@/session/SessionContext';
import { initialsOf, rolesLabel } from '@/ui/format';
import { usePageHeadSlot } from '@/ui/PageHead';

import { useTheme } from './useTheme';

/** Closes the menu on an outside press or Escape. */
function useCloseOnOutside(open: boolean, menuRef: RefObject<HTMLDivElement | null>, close: () => void) {
  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) close();
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') close();
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, menuRef, close]);
}

function UserDropdown({
  user,
  dark,
  onToggleTheme,
  onNavigate,
  onChangePassword,
  onSignOut,
}: {
  user: { display_name: string; email: string; roles: Role[] };
  dark: boolean;
  onToggleTheme: () => void;
  onNavigate: () => void;
  /** Absent for an Entra ID account: its password lives with Microsoft. */
  onChangePassword?: () => void;
  onSignOut: () => void;
}) {
  return (
    <div className="user-dropdown" role="menu">
      <div className="dropdown-head">
        <div className="user-name">{user.display_name}</div>
        <div className="u-email">{user.email}</div>
        <div className="u-email">{rolesLabel(user.roles)}</div>
      </div>
      <Link to="/account" className="dropdown-item" role="menuitem" onClick={onNavigate}>
        <UserRound aria-hidden="true" />
        <span>Account</span>
      </Link>
      <button type="button" className="dropdown-item" role="menuitem" onClick={onToggleTheme}>
        {dark ? <Sun aria-hidden="true" /> : <Moon aria-hidden="true" />}
        <span>{dark ? 'Light theme' : 'Dark theme'}</span>
      </button>
      {onChangePassword && (
        <button type="button" className="dropdown-item" role="menuitem" onClick={onChangePassword}>
          <KeyRound aria-hidden="true" />
          <span>Change password</span>
        </button>
      )}
      <div className="dropdown-sep" />
      <button type="button" className="dropdown-item" role="menuitem" onClick={onSignOut}>
        <LogOut aria-hidden="true" />
        <span>Sign out</span>
      </button>
    </div>
  );
}

export function Header() {
  const { user } = useSession();
  const signOut = useSignOut();
  const { theme, toggle } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const slotRef = usePageHeadSlot();
  const closeMenu = useCallback(() => setMenuOpen(false), []);
  useCloseOnOutside(menuOpen, menuRef, closeMenu);

  if (!user) return null;

  return (
    <header className="app-header">
      <div className="header-page" ref={slotRef} />

      <div className="user-menu" ref={menuRef}>
        <button
          type="button"
          className="user-trigger"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          // The name and role are hidden on a narrow bar (topbar.css); the button keeps its name.
          aria-label={`${user.display_name}, ${rolesLabel(user.roles)}`}
          onClick={() => setMenuOpen((open) => !open)}
        >
          <span className="avatar" aria-hidden="true">
            {initialsOf(user.display_name)}
          </span>
          <span className="user-meta">
            <span className="user-name">{user.display_name}</span>
            <span className="user-role">{rolesLabel(user.roles, true)}</span>
          </span>
          <ChevronDown aria-hidden="true" />
        </button>

        {menuOpen && (
          <UserDropdown
            user={user}
            dark={theme === 'dark'}
            onToggleTheme={toggle}
            onNavigate={closeMenu}
            onSignOut={() => signOut.mutate()}
            onChangePassword={
              user.auth_source === 'local'
                ? () => {
                    setMenuOpen(false);
                    setChangingPassword(true);
                  }
                : undefined
            }
          />
        )}
      </div>

      {changingPassword && <ChangePasswordDialog onClose={() => setChangingPassword(false)} />}
    </header>
  );
}
